import { useState, useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useMotionValue, useSpring } from "framer-motion";
import Marquee from "react-fast-marquee";
import { ArrowLeft, Gift, ChevronDown, Trophy, Flame, Ticket, ShieldCheck, ArrowRight } from "lucide-react";
import { EmberCanvas } from "@/components/EmberCanvas";
import { Countdown } from "@/components/giveaway/Countdown";
import { PaymentBox } from "@/components/giveaway/PaymentBox";
import { EntryForm } from "@/components/giveaway/EntryForm";
import { LiveFeed } from "@/components/giveaway/LiveFeed";
import { useGiveawayStats } from "@/hooks/useGiveawayStats";
import { scrollToEl } from "@/hooks/useLenis";
import { GIVEAWAY, PRIZES, TICKETS, CHAPTERS } from "@/data/giveaway";

const accentMap = {
  gold: { text: "text-gold", border: "border-gold/40", bg: "bg-gold", glow: "glow-gold", ring: "ring-gold/50" },
  ember: { text: "text-ember", border: "border-ember/40", bg: "bg-ember", glow: "glow-ember", ring: "ring-ember/50" },
  blood: { text: "text-blood", border: "border-blood/40", bg: "bg-blood", glow: "glow-ember", ring: "ring-blood/50" },
  solana: { text: "text-solana", border: "border-solana/40", bg: "bg-solana", glow: "glow-purple", ring: "ring-solana/50" },
};

const reveal = {
  hidden: { opacity: 0, y: 40 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: [0.16, 1, 0.3, 1] } },
};

const HeroLine = ({ children, i, className = "" }) => (
  <span className="block overflow-hidden">
    <motion.span
      initial={{ y: "110%" }}
      animate={{ y: 0 }}
      transition={{ duration: 0.9, delay: 0.25 + i * 0.13, ease: [0.16, 1, 0.3, 1] }}
      className={`block ${className}`}
    >
      {children}
    </motion.span>
  </span>
);

export default function Giveaway() {
  const [selectedTier, setSelectedTier] = useState(3);
  const { stats, refetch } = useGiveawayStats();

  const raised = stats?.raised_usd ?? 0;
  const goal = stats?.goal_usd ?? GIVEAWAY.goalUsd;
  const percent = stats?.percent ?? 0;
  const tickets = stats?.tickets_sold ?? 0;
  const entries = stats?.total_entries ?? 0;

  // Hero coin 3D tilt
  const coinRef = useRef(null);
  const rx = useSpring(useMotionValue(0), { stiffness: 120, damping: 15 });
  const ry = useSpring(useMotionValue(0), { stiffness: 120, damping: 15 });
  const onMove = (e) => {
    const r = coinRef.current?.getBoundingClientRect();
    if (!r) return;
    const px = (e.clientX - r.left) / r.width - 0.5;
    const py = (e.clientY - r.top) / r.height - 0.5;
    ry.set(px * 26);
    rx.set(-py * 26);
  };
  const onLeave = () => { rx.set(0); ry.set(0); };

  const buyTier = (tier) => {
    setSelectedTier(tier);
    scrollToEl("#enter");
  };

  return (
    <div className="relative bg-black min-h-screen" data-testid="giveaway-page">
      {/* Slim top bar */}
      <header className="fixed top-0 inset-x-0 z-50 glass border-b border-white/10">
        <div className="max-w-7xl mx-auto px-5 md:px-8 h-16 flex items-center justify-between">
          <Link to="/" data-testid="giveaway-back" className="flex items-center gap-2 text-gray-300 hover:text-gold transition-colors duration-300">
            <ArrowLeft className="w-4 h-4" />
            <span className="font-body text-sm font-bold uppercase tracking-wide hidden sm:inline">Back to $TGB</span>
          </Link>
          <img src={GIVEAWAY.logo} alt="The Greater Bull" className="w-10 h-10 rounded-full object-cover" />
          <button
            onClick={() => scrollToEl("#tickets")}
            data-testid="topbar-enter"
            className="inline-flex items-center gap-2 bg-gradient-to-r from-gold to-ember text-black font-body font-extrabold text-sm px-5 py-2.5 rounded-full uppercase tracking-wide hover:scale-105 transition-transform duration-300"
          >
            <Ticket className="w-4 h-4" /> Get Tickets
          </button>
        </div>
      </header>

      {/* ---------------- HERO ---------------- */}
      <section className="relative min-h-screen flex items-center overflow-hidden grain pt-24 pb-16" data-testid="giveaway-hero">
        <div className="absolute inset-0 z-0">
          <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-[1000px] h-[600px] bg-ember/20 blur-[150px] rounded-full" />
          <div className="absolute top-1/4 left-0 w-[500px] h-[500px] bg-solana/10 blur-[130px] rounded-full" />
          <div className="absolute top-1/3 right-0 w-[500px] h-[500px] bg-blood/15 blur-[130px] rounded-full" />
        </div>
        <EmberCanvas className="absolute inset-0 z-10 w-full h-full" density={80} />

        <div className="relative z-20 max-w-7xl mx-auto px-5 md:px-8 grid lg:grid-cols-2 gap-12 items-center w-full">
          <div>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="inline-flex items-center gap-2 glass rounded-full px-4 py-1.5 mb-6"
            >
              <Gift className="w-4 h-4 text-gold" />
              <span className="font-body text-xs font-bold tracking-widest uppercase text-gray-200">
                The Greater Bull Giveaway
              </span>
            </motion.div>

            <h1 className="font-display text-5xl sm:text-6xl md:text-7xl lg:text-8xl leading-[0.88] uppercase text-white">
              <HeroLine i={0}>Win A</HeroLine>
              <HeroLine i={1} className="fire-gradient-text text-glow-ember">Lamborghini</HeroLine>
              <HeroLine i={2}>$25K Cash</HeroLine>
              <HeroLine i={3}>Or Dubai</HeroLine>
            </h1>

            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.6, delay: 0.9 }}
              className="mt-6 font-body text-lg md:text-xl text-gray-300 max-w-lg leading-relaxed"
            >
              Buy giveaway tickets in crypto to fuel the charge. The more you back the bull,
              the greater your odds of winning legendary prizes.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 1.05 }}
              className="mt-8"
            >
              <p className="text-xs font-body font-bold tracking-widest uppercase text-gray-400 mb-3">Giveaway ends in</p>
              <Countdown />
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 1.2 }}
              className="mt-9 flex flex-col sm:flex-row gap-4"
            >
              <button
                onClick={() => scrollToEl("#tickets")}
                data-testid="hero-get-tickets"
                className="inline-flex items-center justify-center gap-2 bg-gold hover:bg-gold-hover text-black font-body font-extrabold text-base px-8 py-4 rounded-md transition-transform duration-300 hover:scale-105 glow-gold uppercase tracking-wide"
              >
                <Ticket className="w-5 h-5" /> Get Your Tickets
              </button>
              <button
                onClick={() => scrollToEl("#prizes")}
                data-testid="hero-view-prizes"
                className="inline-flex items-center justify-center gap-2 border-2 border-white/20 text-white hover:border-gold hover:text-gold font-body font-bold text-base px-8 py-4 rounded-md transition-colors duration-300 uppercase tracking-wide"
              >
                View Prizes
              </button>
            </motion.div>
          </div>

          {/* Coin with 3D tilt */}
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.9, delay: 0.3 }}
            className="hidden lg:flex justify-center"
            style={{ perspective: 900 }}
          >
            <motion.div
              ref={coinRef}
              onMouseMove={onMove}
              onMouseLeave={onLeave}
              style={{ rotateX: rx, rotateY: ry, transformStyle: "preserve-3d" }}
              className="relative animate-float"
            >
              <div className="absolute inset-0 bg-gold/30 blur-[90px] rounded-full scale-90" />
              <img
                src={GIVEAWAY.logo}
                alt="The Greater Bull $TGB coin"
                className="relative z-10 w-[420px] rounded-full drop-shadow-[0_0_60px_rgba(255,180,0,0.45)]"
                data-testid="hero-coin"
              />
            </motion.div>
          </motion.div>
        </div>

        <motion.button
          onClick={() => scrollToEl("#prizes")}
          animate={{ y: [0, 10, 0] }}
          transition={{ duration: 1.8, repeat: Infinity }}
          className="absolute bottom-6 left-1/2 -translate-x-1/2 z-20 text-gray-500 hover:text-gold transition-colors duration-300"
          aria-label="Scroll down"
        >
          <ChevronDown className="w-7 h-7" />
        </motion.button>
      </section>

      {/* ---------------- MARQUEE ---------------- */}
      <div className="bg-gold border-y-2 border-black py-3 md:py-4 overflow-hidden">
        <Marquee speed={55} gradient={false} autoFill>
          {["THE GREATER BULL GIVEAWAY", "WIN THE LAMBO", "$25,000 CASH", "DUBAI FOR TWO", "FUEL THE CHARGE"].map((it, i) => (
            <span key={i} className="flex items-center">
              <span className="font-display text-xl md:text-2xl uppercase text-black tracking-wide mx-6">{it}</span>
              <Flame className="w-4 h-4 text-black/70" />
            </span>
          ))}
        </Marquee>
      </div>

      {/* ---------------- PRIZES ---------------- */}
      <section id="prizes" className="relative py-24 md:py-32 overflow-hidden" data-testid="prizes-section">
        <div className="absolute top-1/3 left-0 w-[500px] h-[500px] bg-gold/10 blur-[140px] rounded-full" />
        <div className="relative z-10 max-w-7xl mx-auto px-5 md:px-8">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, amount: 0.3 }} variants={reveal} className="text-center mb-16">
            <span className="inline-flex items-center gap-2 text-sm font-body font-bold tracking-widest uppercase text-gold mb-4">
              <Trophy className="w-4 h-4" /> The Prizes
            </span>
            <h2 className="font-display text-4xl md:text-5xl lg:text-6xl uppercase text-white leading-[0.95]">
              Legendary <span className="fire-gradient-text">Rewards</span>
            </h2>
            <p className="mt-4 font-body text-base md:text-lg text-gray-400 max-w-2xl mx-auto">
              Three life-changing prizes. One stampede. Every ticket is a chance to win.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-6">
            {PRIZES.map((prize, i) => {
              const a = accentMap[prize.accent];
              return (
                <motion.div
                  key={prize.id}
                  initial={{ opacity: 0, y: 50 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, amount: 0.3 }}
                  transition={{ duration: 0.6, delay: i * 0.12 }}
                  className={`group relative rounded-lg overflow-hidden border ${a.border} bg-surface`}
                  data-testid={`prize-${prize.id}`}
                >
                  <div className="relative h-64 overflow-hidden">
                    <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-transparent z-10" />
                    <img
                      src={prize.img}
                      alt={prize.title}
                      className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                    />
                    <span className={`absolute top-4 left-4 z-20 text-[11px] font-body font-bold uppercase tracking-widest ${a.text} border ${a.border} rounded-full px-3 py-1 glass`}>
                      {prize.rank}
                    </span>
                  </div>
                  <div className="p-6">
                    <h3 className="font-display text-2xl md:text-3xl uppercase text-white">{prize.title}</h3>
                    <p className="mt-2 font-body text-sm text-gray-400">{prize.subtitle}</p>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ---------------- MANIFESTO ---------------- */}
      <section className="relative py-24 md:py-32 overflow-hidden bg-surface/40" data-testid="manifesto-section">
        <div className="relative z-10 max-w-6xl mx-auto px-5 md:px-8">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, amount: 0.3 }} variants={reveal} className="mb-16 max-w-3xl">
            <span className="inline-flex items-center gap-2 text-sm font-body font-bold tracking-widest uppercase text-ember mb-4">
              <Flame className="w-4 h-4" /> Why We're Raising $1.5M
            </span>
            <h2 className="font-display text-4xl md:text-5xl lg:text-6xl uppercase text-white leading-[0.95]">
              Fueling The <span className="fire-gradient-text">Charge</span>
            </h2>
          </motion.div>

          <div className="divide-y divide-white/10 border-t border-white/10">
            {CHAPTERS.map((c, i) => (
              <motion.div
                key={c.no}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.4 }}
                transition={{ duration: 0.5 }}
                className="grid md:grid-cols-12 gap-4 md:gap-8 py-8 group"
                data-testid={`chapter-${c.no}`}
              >
                <div className="md:col-span-2">
                  <span className="font-display text-5xl md:text-6xl text-white/15 group-hover:text-gold/60 transition-colors duration-500">{c.no}</span>
                </div>
                <h3 className="md:col-span-4 font-display text-2xl md:text-3xl uppercase text-white leading-tight">{c.title}</h3>
                <p className="md:col-span-6 font-body text-base text-gray-400 leading-relaxed">{c.body}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ---------------- PROGRESS ---------------- */}
      <section className="relative py-24 md:py-32 overflow-hidden" data-testid="progress-section">
        <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-[700px] h-[400px] bg-ember/10 blur-[140px] rounded-full" />
        <div className="relative z-10 max-w-4xl mx-auto px-5 md:px-8 text-center">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, amount: 0.3 }} variants={reveal}>
            <span className="text-sm font-body font-bold tracking-widest uppercase text-gold">Marketing War Chest</span>
            <div className="mt-6 flex items-end justify-center gap-3 flex-wrap">
              <span className="font-display text-6xl md:text-8xl fire-gradient-text leading-none" data-testid="raised-amount">
                ${raised.toLocaleString()}
              </span>
              <span className="font-display text-2xl md:text-3xl text-gray-500 mb-1">
                / ${goal.toLocaleString()}
              </span>
            </div>

            <div className="mt-8 h-5 w-full rounded-full bg-white/5 border border-white/10 overflow-hidden">
              <motion.div
                initial={{ width: 0 }}
                whileInView={{ width: `${Math.max(percent, 1.5)}%` }}
                viewport={{ once: true }}
                transition={{ duration: 1.4, ease: [0.16, 1, 0.3, 1] }}
                className="h-full rounded-full bg-gradient-to-r from-gold via-ember to-blood glow-ember"
                data-testid="progress-bar"
              />
            </div>
            <p className="mt-3 font-body text-sm text-gray-400">{percent}% of the $1.5M goal raised</p>

            <div className="mt-10 grid grid-cols-2 gap-4 max-w-md mx-auto">
              <div className="glass rounded-lg p-5" data-testid="stat-tickets">
                <p className="font-display text-3xl text-white">{tickets.toLocaleString()}</p>
                <p className="font-body text-xs uppercase tracking-widest text-gray-400 mt-1">Tickets Sold</p>
              </div>
              <div className="glass rounded-lg p-5" data-testid="stat-entries">
                <p className="font-display text-3xl text-white">{entries.toLocaleString()}</p>
                <p className="font-body text-xs uppercase tracking-widest text-gray-400 mt-1">Total Entries</p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ---------------- LIVE FEED ---------------- */}
      <LiveFeed />

      {/* ---------------- TICKETS ---------------- */}
      <section id="tickets" className="relative py-24 md:py-32 overflow-hidden bg-surface/40" data-testid="tickets-section">
        <div className="relative z-10 max-w-7xl mx-auto px-5 md:px-8">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, amount: 0.3 }} variants={reveal} className="text-center mb-14">
            <span className="inline-flex items-center gap-2 text-sm font-body font-bold tracking-widest uppercase text-solana mb-4">
              <Ticket className="w-4 h-4" /> Buy Tickets
            </span>
            <h2 className="font-display text-4xl md:text-5xl lg:text-6xl uppercase text-white leading-[0.95]">
              Pick Your <span className="fire-gradient-text">Odds</span>
            </h2>
            <p className="mt-4 font-body text-base md:text-lg text-gray-400 max-w-2xl mx-auto">
              The higher you buy, the more entries you get — and the greater your chance to win.
            </p>
          </motion.div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {TICKETS.map((t, i) => {
              const a = accentMap[t.accent];
              return (
                <motion.div
                  key={t.tier}
                  initial={{ opacity: 0, y: 40 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, amount: 0.3 }}
                  transition={{ duration: 0.5, delay: i * 0.1 }}
                  className={`relative flex flex-col rounded-lg p-7 border ${a.border} glass ${t.popular ? "ring-2 " + a.ring : ""}`}
                  data-testid={`ticket-${t.tier}`}
                >
                  {t.popular && (
                    <span className={`absolute -top-3 left-1/2 -translate-x-1/2 text-[10px] font-body font-extrabold uppercase tracking-widest text-black ${a.bg} rounded-full px-3 py-1`}>
                      Best Value
                    </span>
                  )}
                  <p className={`font-body text-xs font-bold uppercase tracking-widest ${a.text}`}>{t.name}</p>
                  <p className="font-display text-5xl text-white mt-3 leading-none">${t.price}</p>
                  <div className={`mt-4 inline-flex items-center gap-2 ${a.text}`}>
                    <Flame className="w-4 h-4" />
                    <span className="font-display text-2xl">{t.entries}x</span>
                    <span className="font-body text-xs text-gray-400 uppercase tracking-wide">
                      {t.entries > 1 ? "entries" : "entry"}
                    </span>
                  </div>
                  <ul className="mt-5 space-y-2 flex-1">
                    {t.perks.map((p, j) => (
                      <li key={j} className="font-body text-sm text-gray-400 flex items-start gap-2">
                        <Flame className="w-3.5 h-3.5 text-ember/60 shrink-0 mt-0.5" /> {p}
                      </li>
                    ))}
                  </ul>
                  <button
                    onClick={() => buyTier(t.tier)}
                    data-testid={`ticket-buy-${t.tier}`}
                    className={`mt-6 w-full inline-flex items-center justify-center gap-2 font-body font-extrabold text-sm px-5 py-3 rounded-md uppercase tracking-wide transition-transform duration-300 hover:scale-105 ${
                      t.popular ? `${a.bg} text-black ${a.glow}` : "border border-white/20 text-white hover:border-gold hover:text-gold"
                    }`}
                  >
                    Buy {t.name}
                  </button>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ---------------- ENTER (PAYMENT + FORM) ---------------- */}
      <section id="enter" className="relative py-24 md:py-32 overflow-hidden" data-testid="enter-section">
        <div className="absolute top-1/4 right-0 w-[500px] h-[500px] bg-solana/10 blur-[140px] rounded-full" />
        <div className="relative z-10 max-w-6xl mx-auto px-5 md:px-8">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, amount: 0.3 }} variants={reveal} className="text-center mb-14">
            <span className="inline-flex items-center gap-2 text-sm font-body font-bold tracking-widest uppercase text-ember mb-4">
              <ShieldCheck className="w-4 h-4" /> How To Enter
            </span>
            <h2 className="font-display text-4xl md:text-5xl lg:text-6xl uppercase text-white leading-[0.95]">
              Pay & <span className="fire-gradient-text">Register</span>
            </h2>
            <p className="mt-4 font-body text-base md:text-lg text-gray-400 max-w-2xl mx-auto">
              Send your ticket amount in crypto, then register your entry below. It takes under a minute.
            </p>
          </motion.div>

          <div className="grid lg:grid-cols-2 gap-8 items-start">
            <div className="space-y-6">
              <div className="flex items-start gap-4">
                <span className="shrink-0 w-9 h-9 rounded-md bg-gold/10 border border-gold/30 flex items-center justify-center font-display text-gold">1</span>
                <p className="font-body text-gray-300 pt-1">Choose a ticket tier and send the exact USD value in <span className="text-white font-semibold">SOL or USDC on Solana</span> to the wallet below.</p>
              </div>
              <div className="flex items-start gap-4">
                <span className="shrink-0 w-9 h-9 rounded-md bg-gold/10 border border-gold/30 flex items-center justify-center font-display text-gold">2</span>
                <p className="font-body text-gray-300 pt-1">Copy your <span className="text-white font-semibold">transaction hash</span> from your wallet or Solscan.</p>
              </div>
              <div className="flex items-start gap-4">
                <span className="shrink-0 w-9 h-9 rounded-md bg-gold/10 border border-gold/30 flex items-center justify-center font-display text-gold">3</span>
                <p className="font-body text-gray-300 pt-1">Fill out the form with your email, wallet and TX hash. Your entries are locked in instantly.</p>
              </div>
              <PaymentBox />
            </div>

            <EntryForm selectedTier={selectedTier} setSelectedTier={setSelectedTier} onSuccess={refetch} />
          </div>
        </div>
      </section>

      {/* ---------------- FOOTER ---------------- */}
      <footer className="relative border-t border-white/10 bg-surface/60 py-14" data-testid="giveaway-footer">
        <div className="max-w-7xl mx-auto px-5 md:px-8">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6 pb-10">
            <div className="flex items-center gap-3">
              <img src={GIVEAWAY.logo} alt="The Greater Bull" className="w-12 h-12 rounded-full object-cover" />
              <div>
                <p className="font-display text-xl text-white uppercase">The Greater Bull</p>
                <a href={GIVEAWAY.mainSite} className="font-body text-sm text-gray-400 hover:text-gold transition-colors duration-300">TheGreaterBull.com</a>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <Link to="/" data-testid="footer-home" className="font-body text-sm font-bold uppercase tracking-wide text-gray-300 hover:text-gold transition-colors duration-300">Main Site</Link>
              <button onClick={() => scrollToEl("#tickets")} className="inline-flex items-center gap-2 bg-gradient-to-r from-gold to-ember text-black font-body font-extrabold text-sm px-5 py-2.5 rounded-full uppercase tracking-wide">
                <Ticket className="w-4 h-4" /> Get Tickets <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          </div>
          <div className="border-t border-white/10 pt-6">
            <p className="font-body text-xs text-gray-500 leading-relaxed max-w-4xl">
              <span className="text-gray-300 font-semibold">Disclaimer:</span> The Greater Bull ($TGB) giveaway is a community-funded promotional event. Contract address:{" "}
              <span className="font-mono text-gray-400 break-all">FWAL41iCEHYncRYi24Cmtz7onu8UwafhZviTta7Wpump</span>. Participation involves the purchase of giveaway tickets using cryptocurrency and carries risk. Ticket purchases are non-refundable and do not guarantee a prize. Odds increase with the number of entries. You must be of legal age in your jurisdiction to participate. This is not financial advice — crypto is highly volatile, always DYOR. Void where prohibited.
            </p>
            <p className="mt-4 font-body text-xs text-gray-600">© {new Date().getFullYear()} The Greater Bull ($TGB). All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
